import { takeUntil } from 'rxjs/operators';
import { isEmpty } from 'lodash';
import { Moment } from 'moment';
import { formatBillingIntervalDays } from '../_helpers';
import { MultiOption, MultiSubOption, FilterInputType } from '../custom-multi-select/multi-select';
import { ReportsAbstractV2Component } from './reports-abstract-v2.component';
// stage option constants used directly in stageOptions getter
import {
  mergedDimensionFields,
  ignoredSummaryFields,
  defaultOfferGroupFields,
  offerGroupHeaders,
  sortableOfferGroupKeys,
  OfferNavKeys,
  subRetentionStageOptions,
  orderRetentionStageOptions,
  returnRetentionStageOptions,
  portalMomentColumns,
  topIntentColumns,
} from './utils/constants';
import {
  BaseFilterOptionsQuery,
  CRMCampaignFilterResult,
  CRMCampaignsFilterOptionsQuery,
  CustomerJourneyBoolFilter,
  CustomerJourneyCell,
  CustomerJourneyType,
  GatewayDriverStatus,
  GatewayDimensionOptionsResponse,
  GatewayFilterOption,
  GatewayLiveDriverCard,
  PortalActionRow,
  PortalMomentRow,
  ReportOverviewCard,
  ReportPanelColumn,
  TopIntentRow,
  GatewayPerformanceDriver,
  GatewayTopBar,
  OfferGroupItem,
  OverviewTab,
  ProductFilterResult,
  ProductsFilterOptionsQuery,
  ReportDimensionLabels,
  ReportDimensions,
  ReportTypes,
} from './models/reports';
import {
  DashboardNavFilters,
  FunnelInputTypeEnum,
  OfferIntents,
  OfferTypes,
  ReportsUserConfiguration,
  StepCategoryLabels,
} from '../_models';
import { Router, ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import {
  AccountService,
  AlertService,
  CampaignProductService,
  CampaignService,
  CampaignTrackingService,
  CancelReasonCategoryService,
  CRMCampaignService,
  FunnelService,
  FunnelStepService,
  GuiService,
  MerchantService,
  ReportsAbstractService,
  ReportsConfigService,
  StorageService,
  UserService,
} from '../_services';
import { NgxSmartModalService } from 'ngx-smart-modal';
import * as moment from 'moment';

export abstract class ReportsAbstractV3Component extends ReportsAbstractV2Component {
  isV3Report: boolean = true;
  protected fieldData = null;
  protected pctFieldMap: { [key: string]: string } = {};
  protected overviewFieldMap: { [summaryField: string]: string } = {};
  protected offerApiKeyMap: { [fieldKey: string]: string } = {
    impressions: 'total_impressions',
    accepted: 'total_accepted',
  };
  bannerTitle: string = null;
  bannerSubtitle: string = null;
  summaryExpanded: boolean = false;
  driversExpanded: boolean = false;
  OverviewTab = OverviewTab;
  GatewayDriverStatus = GatewayDriverStatus;
  activeOverviewTab = OverviewTab.Retention;
  showPercentage: boolean = false;
  showExcludeUpsellsFilter: boolean = true;
  showExcludeValidationsFilter: boolean = true;
  fetchesFilterOptionsOnInit: boolean = true;
  customerStageLoading: boolean = false;
  offerGroupFields: string[] = defaultOfferGroupFields;
  hasMoreProducts: boolean = false;
  private activeProductSearchTerm: string = '';
  topBannerDismissed: boolean = false;
  selectedOfferIntent: number | string = '';
  availableReports: ReportsUserConfiguration[] = [];
  selectedLoadReportId: string | number = null;
  isLoadingReports: boolean = false;

  selectedDimensions: { model: string; label: string; selected: boolean; disabled: boolean }[] = [];
  protected dashboardNavFilters: DashboardNavFilters = {};

  selectedGateway: number | string = '';
  selectedStage: string = '';
  countType: string = '';

  // A report opts into these panels by setting the flag; the template never checks report type.
  showOverviewPanel: boolean = false;
  showDriversPanel: boolean = false;
  showTopIntentsPanel: boolean = false;
  showPortalMomentsPanel: boolean = false;
  showPortalActionsPanel: boolean = false;
  topIntents: TopIntentRow[] = [];
  portalMoments: PortalMomentRow[] = [];
  portalActions: PortalActionRow[] = [];
  topIntentsPanelColumns: ReportPanelColumn[] = topIntentColumns;
  momentsPanelTitle: string = 'Portal Moments';
  momentsPanelColumns: ReportPanelColumn[] = portalMomentColumns;

  // Top bar and menu parts a report can turn off or fill; the Customer Journey Report has no date range or templates.
  showDateRange: boolean = true;
  showTemplateMenu: boolean = true;
  showRequiredMarkers: boolean = true;
  showPercentageToggle: boolean = true;
  downloadMenuLabel: string = 'Save Report';
  topBarFields: CustomerJourneyCell[] = [];
  topBarToggles: CustomerJourneyBoolFilter[] = [];

  overviewCards: ReportOverviewCard[] = [];
  overviewCardGroups: ReportOverviewCard[][] = [];
  performanceDrivers: GatewayPerformanceDriver[] = [];
  topBarOptions: GatewayTopBar = null;
  dimensionOptionsResponse: GatewayDimensionOptionsResponse = null;
  isOverviewLoading: boolean = false;
  isDriversLoading: boolean = false;
  isTopBarLoading: boolean = true;
  protected navStartDate: string = null;
  protected navEndDate: string = null;

  offerGroupHeaders = offerGroupHeaders;

  offerGroupRows: Record<string, string | number | null>[] = [];

  offerGroupSortKey: string = null;
  offerGroupSortAsc: boolean = false;
  sortableOfferGroupKeys: string[] = sortableOfferGroupKeys;

  // Fetch date-dependent filter options first, then run the report so the payload reflects the new options.
  onDateChange = (dateRange: { startDate: Moment; endDate: Moment }) => {
    if (dateRange.startDate !== null && dateRange.endDate !== null) {
      this.date = {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      };
      if (!this.isRetentionReport) {
        this.getReport(true);
      } else {
        if (!isEmpty(this.summary)) {
          this.isReportGenerating = true;
          this.isSummaryLoading = true;
          if (this.reportsTable) {
            this.reportsTable.isTableLoading = true;
          }
        }
        this.getDateDependentFiltersData();
      }
    }
  };

  constructor(
    protected router: Router,
    protected location: Location,
    protected route: ActivatedRoute,
    protected alertService: AlertService,
    public reportsService: ReportsAbstractService,
    protected campaignService: CampaignService,
    protected reportsConfigService: ReportsConfigService,
    protected trackingSourceService: CampaignTrackingService,
    protected userService: UserService,
    protected accountService: AccountService,
    protected campaignProductService: CampaignProductService,
    protected crmCampaignService: CRMCampaignService,
    protected storageService: StorageService,
    protected stepService: FunnelStepService,
    protected merchantService: MerchantService,
    protected modalService: NgxSmartModalService,
    protected cancelReasonCategoryService: CancelReasonCategoryService,
    protected funnelService: FunnelService,
    protected guiService: GuiService
  ) {
    super(
      router,
      location,
      route,
      alertService,
      reportsService,
      campaignService,
      reportsConfigService,
      trackingSourceService,
      userService,
      accountService,
      campaignProductService,
      crmCampaignService,
      storageService,
      stepService,
      merchantService,
      modalService,
      cancelReasonCategoryService
    );
  }

  ngOnInit() {
    const dim: string = this.route.snapshot.params['dimension'];
    this.initFromDashboardNav(dim);
    super.ngOnInit();

    if (dim) {
      if (dim !== ReportDimensions.RetentionSummary) {
        this.preselectNavDimension(dim);
      }
      if (this.dashboardNavFilters.product_id) {
        this.preselectNavProduct();
      }
    }

    if (this.fetchesFilterOptionsOnInit) {
      this.getFilterOptionsData(this.getFilterOptionConfig());
    }
    if (this.isRetentionReport) {
      this.fetchSegmentFilterOptions();
    }
    this.fetchAvailableReports();

    if (this.isGatewayReport) {
      this.loadOverviewCards();
      this.loadPerformanceDrivers();
      this.loadGatewayFilterOptions();
    }
  }

  protected initFromDashboardNav(dim: string) {
    if (dim && dim !== ReportDimensions.RetentionSummary && dim !== ReportDimensions.CancelReason) {
      if (this.selectedDimensions[0]) {
        this.deselectNavDimension(this.selectedDimensions[0].model);
      }
      this.selectedDimensions[0] = {
        model: dim,
        label: ReportDimensionLabels[dim] || dim,
        selected: true,
        disabled: false,
      };
    }
    const f: DashboardNavFilters = this.storageService.get('dashboard_nav_filters');
    if (f) {
      if (f.start_date && f.end_date) {
        this.navStartDate = f.start_date;
        this.navEndDate = f.end_date;
      }
      if (f.offer_intent !== undefined && f.offer_intent !== null) {
        this.selectedOfferIntent = f.offer_intent;
      }
      if (f.campaign_id) {
        this.dashboardNavFilters.campaign_id = f.campaign_id;
        this.selectedCampaign = f.campaign_id;
      }
      if (f.product_id) {
        this.dashboardNavFilters.product_id = f.product_id;
        this.selectedProductMap.set(f.product_id, {
          model: f.product_id,
          label: f.product_name ? f.product_name + ' (' + f.product_id + ')' : String(f.product_id),
          selected: true,
          disabled: false,
        });
      }
      if (f.exclude_abandons) {
        this.excludeAbandons = true;
      }
      if (f.exclude_prefulfillment_cancels) {
        this.excludePrefulfillmentCancels = true;
      }
      this.storageService.remove('dashboard_nav_filters');
    }
  }

  initializeDate() {
    super.initializeDate();
    if (this.navStartDate && this.navEndDate) {
      this.date = {
        startDate: moment(this.navStartDate, 'YYYY-MM-DD HH:mm:ss'),
        endDate: moment(this.navEndDate, 'YYYY-MM-DD HH:mm:ss'),
      };
    }
  }

  protected applyDashboardNavFilters(query: any) {
    if (this.dashboardNavFilters.campaign_id || this.dashboardNavFilters.product_id) {
      if (!query['filters']) {
        query['filters'] = {};
      }
      if (this.dashboardNavFilters.campaign_id) {
        query['filters']['campaign_id'] = [Number(this.dashboardNavFilters.campaign_id)];
      }
      if (this.dashboardNavFilters.product_id) {
        query['filters']['product_id'] = [this.dashboardNavFilters.product_id];
      }
    }
  }

  private deselectNavDimension(dim: string) {
    this.fields.forEach((category) => {
      if (category.options) {
        const option = (category.options as MultiSubOption[]).find((opt) => opt.model === dim);
        if (option) {
          option.selected = false;
        }
      }
    });
  }

  private preselectNavDimension(dim: string) {
    let found = false;
    this.fields.forEach((category) => {
      if (!found && category.options) {
        const option = (category.options as MultiSubOption[]).find((opt) => opt.model === dim);
        if (option) {
          option.selected = true;
          category.activeCount = (category.activeCount || 0) + 1;
          found = true;
        }
      }
    });
  }

  private preselectNavProduct() {
    const productFilter = this.findFilter(ReportDimensions.ProductID);
    if (productFilter) {
      productFilter.activeCount = 1;
      const parentCategory = this.filters.find(
        (cat) => cat.options && (cat.options as MultiOption[]).some((o) => o.model === ReportDimensions.ProductID)
      );
      if (parentCategory) {
        parentCategory.activeCount = (parentCategory.options as MultiOption[]).reduce(
          (sum, ft: MultiOption) => sum + (ft.activeCount || 0),
          0
        );
      }
      const alreadyInSelected = this.selectedFilters.find((f) => f.model === productFilter.model);
      if (!alreadyInSelected) {
        this.selectedFilters.push(productFilter);
      }
    }
    this.getProducts({ searchTerm: '', nextPage: false });
  }

  fetchSegmentFilterOptions() {
    if (this.isRetentionReport) {
      let audienceFilterModel = ReportDimensions.HoldAudienceID;
      if (this.isSubRetention) {
        audienceFilterModel = ReportDimensions.SubscriptionAudienceID;
      } else if (this.isReturnRetention) {
        audienceFilterModel = ReportDimensions.RmaAudienceID;
      }
      const audienceFilter = this.findFilter(audienceFilterModel);

      if (audienceFilter) {
        audienceFilter.loading = true;

        let offerIntent: number | number[];
        if (this.isSubRetention) {
          offerIntent = OfferIntents.CancelSubscription;
        } else if (this.selectedOfferIntent) {
          offerIntent = this.selectedOfferIntent as number;
        } else {
          offerIntent = this.stageOptions.map((o) => o.value);
        }

        this.funnelService
          .listSegments(offerIntent)
          .pipe(takeUntil(this._destroy$))
          .subscribe(
            (data) => {
              const options = [];
              if (Array.isArray(data.results)) {
                data.results.forEach((item: any) => {
                  if (item.audience) {
                    options.push({
                      model: item.audience.id,
                      label: item.audience.name || String(item.audience.id),
                      selected: false,
                      disabled: false,
                    });
                  }
                });
              }
              audienceFilter.options = options;
              audienceFilter.loading = false;
            },
            (error) => {
              audienceFilter.loading = false;
              this.handleError(error);
            }
          );
      }
    }
  }

  onOfferIntentChange() {
    if (this.isRetentionReport) {
      let segmentModel = ReportDimensions.HoldAudienceID;
      if (this.isSubRetention) {
        segmentModel = ReportDimensions.SubscriptionAudienceID;
      } else if (this.isReturnRetention) {
        segmentModel = ReportDimensions.RmaAudienceID;
      }
      const segmentFilter = this.findFilter(segmentModel);
      if (segmentFilter && segmentFilter.activeCount > 0) {
        const selected = this.selectedFilters.find((f) => f.model === segmentModel);
        if (selected) {
          this.removeFilter(selected);
        }
      }
      this.fetchSegmentFilterOptions();
    }
  }

  getFilterOptionsData(fields: ReportDimensions[]) {
    if (fields.length > 0) {
      const query = this.getQueryForSummary();
      query['fields'] = fields;
      this.reportsService
        .getFilterOptions(query)
        .pipe(takeUntil(this._destroy$))
        .subscribe(
          (data) => {
            fields.forEach((key) => {
              const filterItem = this.findFilter(key);
              if (filterItem && data[key] && Array.isArray(data[key])) {
                filterItem.options = data[key].map((val) => {
                  let label = String(val);
                  if (key === ReportDimensions.OfferType) {
                    label = StepCategoryLabels[val];
                  } else if (key === ReportDimensions.BillingIntervalDays) {
                    label = formatBillingIntervalDays(val);
                  }
                  return { model: val, label, selected: false, disabled: false };
                });
                filterItem.loading = false;
              } else if (filterItem) {
                filterItem.loading = false;
              }
            });
            this.getCampaignsList();
            this.dropdownsLoading = false;
          },
          (error) => {
            this.dropdownsLoading = false;
            this.handleError(error);
          }
        );
    } else {
      this.getCampaignsList();
      this.dropdownsLoading = false;
    }
  }

  protected buildSummaryData(overview: { [key: string]: number | string }): { [label: string]: string } {
    const summaryData = {};
    this.summaryFields.forEach((key) => {
      const entry = this.fieldData[key];
      if (!ignoredSummaryFields.includes(key) && entry) {
        const formattedKey = entry.summary_label || entry.custom_label || entry.label;
        const apiKey = this.overviewFieldMap[key] || key;
        let value = '--';
        if (overview[apiKey] !== undefined && 'getter' in entry) {
          const formatted = entry.getter({}, overview[apiKey]);
          if (formatted !== null && formatted !== undefined && formatted !== '') {
            value = formatted;
          }
        }
        summaryData[formattedKey] = value;
      }
    });
    return summaryData;
  }

  getSummary() {
    if (this.isRetentionReport) {
      this.isSummaryLoading = true;
      this.getSummaryApiCalls()
        .pipe(takeUntil(this._destroy$))
        .subscribe(
          (result) => {
            const response = result && result[0] ? result[0] : {};
            const overview = response.overview ? response.overview : {};
            const offers = response.offers && Array.isArray(response.offers) ? response.offers : [];

            this.summary = this.buildSummaryData(overview);

            this.offerGroupRows = offers.map((offer: OfferGroupItem) => {
              const row: Record<string, string | number | null> = {};
              this.offerGroupFields.forEach((field) => {
                if (field === ReportDimensions.Position && this.isLegacyOffer(offer)) {
                  row[field] = '--';
                } else {
                  const apiKey = this.offerApiKeyMap[field] || field;
                  const raw = offer[apiKey] !== undefined ? offer[apiKey] : null;
                  const entry = this.fieldData[field];
                  row[field] = entry && 'getter' in entry ? entry.getter(offer, raw) : raw;
                }
              });
              const navFields = this.offerNavFields;
              row[OfferNavKeys.stageId] = offer[navFields.pathId] as string | number;
              row[OfferNavKeys.intent] = offer[navFields.intent] as number;
              row[OfferNavKeys.funnelType] = offer[navFields.funnelType] as number;
              row[OfferNavKeys.offerId] = offer['offer_id'] as string;
              row[OfferNavKeys.category] = offer['offer_type'] as number;
              return row;
            });

            this.isSummaryLoading = false;
          },
          (error) => {
            this.summary = this.buildSummaryData({});
            this.isSummaryLoading = false;
            this.handleError(error);
          }
        );
    }
  }

  sortOfferGroup(key: string) {
    if (this.offerGroupSortKey === key) {
      this.offerGroupSortAsc = !this.offerGroupSortAsc;
    } else {
      this.offerGroupSortKey = key;
      this.offerGroupSortAsc = false;
    }
    const asc = this.offerGroupSortAsc;
    const parseVal = (v: string | number | null): number => {
      let result = 0;
      if (v !== null && v !== undefined && v !== '--') {
        if (typeof v === 'number') {
          result = v;
        } else {
          const str = String(v).replace(/[$,%]/g, '').trim();
          const num = parseFloat(str);
          if (!isNaN(num)) {
            result = num;
          }
        }
      }
      return result;
    };
    this.offerGroupRows = this.offerGroupRows.slice().sort((a, b) => {
      const aVal = parseVal(a[key]);
      const bVal = parseVal(b[key]);
      let comparison = bVal - aVal;
      if (asc) {
        comparison = aVal - bVal;
      }
      return comparison;
    });
  }

  protected normalizeQueryFilters(query: any) {
    if (query['filters'] && query['filters']['campaign_id']) {
      query['filters']['campaign_id'] = [Number(query['filters']['campaign_id'])];
    }
    if (this.isGatewayReport) {
      if (this.selectedStage) {
        query['stage'] = this.selectedStage;
      }
      if (this.countType) {
        query['count_type'] = this.countType;
      }
      if (this.selectedGateway) {
        query['filters']['gateway_id'] = this.selectedGateway;
      }
    }
    if (this.isRetentionReport) {
      if (this.selectedOfferIntent) {
        let intentKey = 'subscription_intent';
        if (this.isOrderRetention) {
          intentKey = 'hold_intent';
        } else if (this.isReturnRetention) {
          intentKey = 'rma_intent';
        }
        query['filters'][intentKey] = this.selectedOfferIntent;
      }
      if (query['fields'] && query['fields'].includes('offer_position')) {
        let funnelTypeField = 'subscription_funnel_type';
        if (this.isOrderRetention) {
          funnelTypeField = 'hold_funnel_type';
        } else if (this.isReturnRetention) {
          funnelTypeField = 'rma_funnel_type';
        }
        if (!query['fields'].includes(funnelTypeField)) {
          query['fields'].push(funnelTypeField);
        }
      }
      const filters = query['filters'] as { [key: string]: any };
      if (filters && filters['!cancel_reason']) {
        delete filters['!cancel_reason'];
      }
      if (this.showExcludeValidationsFilter && this.excludeValidations) {
        query['fields'].push('exclude_validations');
      }
      query['date_filter_type'] = 'session';
    }
  }

  getQueryForReport() {
    const query = super.getQueryForReport();
    if (this.showPercentage) {
      const pctFields: string[] = [];
      this.selectedDimensionsList.forEach((field) => {
        if (this.pctFieldMap[field]) {
          pctFields.push(this.pctFieldMap[field]);
        }
      });
      if (pctFields.length > 0) {
        query['fields'] = query['fields'].concat(pctFields);
      }
    }
    this.normalizeQueryFilters(query);
    return query;
  }

  // Rebuilds filter options after a date change: preserves selected state, syncs activeCount, drops chip if nothing remains.
  private rebuildDateDependentFilterOptions(dimensionKey: ReportDimensions, newValues: string[]): void {
    const filterItem = this.findFilter(dimensionKey);
    if (filterItem) {
      const prevOptions = (filterItem.options as MultiOption[]) || [];
      filterItem.options = newValues.map((val: string) => {
        const prev = prevOptions.find((o: MultiOption) => o.model === val);
        return {
          model: val,
          label: String(val),
          selected: prev ? prev.selected : false,
          disabled: false,
        };
      });
      filterItem.activeCount = (filterItem.options as MultiOption[]).filter(
        (o: MultiOption) => o.selected
      ).length;
      filterItem.loading = false;

      // Sync the parent category's activeCount so its checkmark stays accurate
      const parentCategory = this.filters.find(
        (cat) => cat.options && (cat.options as MultiOption[]).includes(filterItem)
      );
      if (parentCategory) {
        parentCategory.activeCount = (parentCategory.options as MultiOption[]).reduce(
          (sum: number, ft: MultiOption) => sum + (ft.activeCount || 0),
          0
        );
      }

      // If none of the previously selected values survive the new date range, clear the chip
      if (filterItem.activeCount === 0) {
        this.selectedFilters = this.selectedFilters.filter(
          (sf) => String(sf.model) !== String(dimensionKey)
        );
      }
    }
  }

  getDateDependentFiltersData() {
    this.customerStageLoading = true;
    const query = this.getQueryForSummary();
    query['fields'] = [ReportDimensions.CustomerStage, ReportDimensions.CancelReason, ReportDimensions.CRMName];
    if (this.showExcludeValidationsFilter && this.excludeValidations) {
      query['report_fields'] = ['exclude_validations'];
    }
    this.reportsService
      .getFilterOptions(query)
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        (data) => {
          this.customerStageOptions = data[ReportDimensions.CustomerStage] || [];

          const cancelReasonFilter = this.findFilter(ReportDimensions.CancelReason);
          if (cancelReasonFilter && data[ReportDimensions.CancelReason] && Array.isArray(data[ReportDimensions.CancelReason])) {
            this.rebuildDateDependentFilterOptions(ReportDimensions.CancelReason, data[ReportDimensions.CancelReason]);
          } else if (cancelReasonFilter) {
            cancelReasonFilter.loading = false;
          }

          const crmNameFilter = this.findFilter(ReportDimensions.CRMName);
          if (crmNameFilter && data[ReportDimensions.CRMName] && Array.isArray(data[ReportDimensions.CRMName])) {
            this.rebuildDateDependentFilterOptions(ReportDimensions.CRMName, data[ReportDimensions.CRMName]);
          } else if (crmNameFilter) {
            crmNameFilter.loading = false;
          }

          this.customerStageLoading = false;

          if (!isEmpty(this.summary)) {
            this.getReport(true);
          }
        },
        (error) => {
          this.handleError(error);
          this.customerStageLoading = false;
          if (!isEmpty(this.summary)) {
            this.getReport(true);
          }
        }
      );
  }

  protected buildSelectedFiltersMap(): { [key: string]: any } {
    const result = super.buildSelectedFiltersMap();
    if (result['customer_stage']) {
      if (this.isSubRetention) {
        result['subscription_path'] = result['customer_stage'];
      } else if (this.isOrderRetention) {
        result['hold_path'] = result['customer_stage'];
      } else if (this.isReturnRetention) {
        result['rma_path'] = result['customer_stage'];
      }
      delete result['customer_stage'];
    }
    this.selectedFilters.forEach((selectedFilter) => {
      const matchingFilter = this.findFilter(String(selectedFilter.model));
      console.log(matchingFilter, selectedFilter);
      if (matchingFilter && matchingFilter.type === 'INPUT') {
        const key = String(selectedFilter.model);
        if (result[key] !== undefined && result[key] !== null && result[key] !== '') {
          if (matchingFilter.inputType === FilterInputType.Text) {
            result[key] = String(result[key]);
          } else {
            result[key] = Number(result[key]);
          }
        }
      }
    });
    return result;
  }

  onTopBarToggleChange() {
    this.isConfigChanged = true;
  }

  protected buildOverviewCards(
    metrics: string[],
    overview: { [metric: string]: number },
    summary: { [label: string]: string }
  ): ReportOverviewCard[] {
    return metrics.map((key) => {
      const entry = this.fieldData[key];
      const label = entry ? entry.summary_label || entry.custom_label || entry.label : key;
      return {
        key,
        label,
        value: overview[key],
        display: summary[label],
        helpText: entry ? entry.summaryHelpText : '',
      };
    });
  }

  protected setOverviewCards(cards: ReportOverviewCard[]): void {
    const groups: ReportOverviewCard[][] = [];
    for (let i = 0; i < cards.length; i += 4) {
      groups.push(cards.slice(i, i + 4));
    }
    this.overviewCards = cards;
    this.overviewCardGroups = groups;
  }

  protected loadOverviewCards(): void {
    this.isOverviewLoading = true;
    this.reportsService
      .getOverviewCards(this.getQueryForSummary())
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        (data) => {
          this.setOverviewCards(data.cards || []);
          this.isOverviewLoading = false;
        },
        (error) => {
          this.isOverviewLoading = false;
          this.handleError(error);
        }
      );
  }

  protected loadPerformanceDrivers(): void {
    this.isDriversLoading = true;
    this.reportsService
      .getPerformanceDrivers(this.getQueryForSummary())
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        (data) => {
          this.performanceDrivers = data.cards || [];
          this.isDriversLoading = false;
        },
        (error) => {
          this.isDriversLoading = false;
          this.handleError(error);
        }
      );
  }

  formatOverviewCardValue(card: ReportOverviewCard): string {
    let result = '--';
    if (card.display !== null && card.display !== undefined && card.display !== '') {
      result = card.display;
    } else if (card.value !== null && card.value !== undefined) {
      const key = card.key;
      const isRate = key.includes('rate') || key.includes('pct') || key.includes('percentage');
      const explicitCurrencyKeys = ['collected_from_earlier', 'pending_settlement'];
      const isCurrency = key.includes('amount') || key.includes('revenue') ||
        key.includes('value') || explicitCurrencyKeys.indexOf(key) !== -1;
      if (isRate) {
        result = card.value + '%';
      } else if (isCurrency) {
        result = '$' + card.value;
      } else {
        result = String(card.value);
      }
    }
    return result;
  }

  getDriverDisplayValue(driver: GatewayPerformanceDriver): string {
    let result = '--';
    if (driver.status === GatewayDriverStatus.Live) {
      const liveDriver = driver as GatewayLiveDriverCard;
      if (liveDriver.key === 'three_ds_approval_rate') {
        result = liveDriver.with_3ds + '% / ' + liveDriver.without_3ds + '%';
      } else if (liveDriver.key === 'token_approval_rate') {
        result = liveDriver.tokenized + '% / ' + liveDriver.non_tokenized + '%';
      } else if (liveDriver.value !== null && liveDriver.value !== undefined) {
        result = liveDriver.value;
      }
    }
    return result;
  }

  protected loadGatewayFilterOptions(): void {
    this.reportsService
      .getFilterOptions(this.getQueryForSummary())
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        (data: any) => {
          this.topBarOptions = data.top_bar || (data.gateways ? data : null);
          this.isTopBarLoading = false;
          this.updateFiltersFromOptions(data.filters || []);
          this.getCampaignsList();
        },
        (error) => {
          this.isTopBarLoading = false;
          this.handleError(error);
        }
      );
  }

  private updateFiltersFromOptions(filterOptions: GatewayFilterOption[]): void {
    filterOptions.forEach((opt) => {
      const filterItem: MultiOption = this.findFilter(opt.key);
      if (filterItem) {
        if (opt.status === GatewayDriverStatus.Deferred) {
          filterItem.disabled = true;
          filterItem.unavailable = true;
          filterItem.loading = false;
        } else {
          if (opt.values && opt.values.length > 0) {
            filterItem.options = opt.values.map((val: string) => ({
              model: val,
              label: val,
              selected: false,
              disabled: false,
            }));
          }
          filterItem.loading = false;
        }
      }
    });
  }

  protected getFilterOptionConfig(): ReportDimensions[] {
    const fields: ReportDimensions[] = [];

    if (this.isRetentionReport) {
      fields.push(
        ReportDimensions.CustomerStage,
        ReportDimensions.ProductVariant,
        ReportDimensions.DeviceType,
        ReportDimensions.OSName,
        ReportDimensions.BrowserName,
        ReportDimensions.DeviceBrandFilter,
        ReportDimensions.CancelReason,
        ReportDimensions.CancelReasonCategoryName,
        ReportDimensions.CRMName,
        ReportDimensions.TrafficSource,
        ReportDimensions.OfferType,
      );

      if (this.isOrderRetention) {
        fields.push(ReportDimensions.ProductRole);
      }
      if (this.isSubRetention) {
        fields.push(ReportDimensions.BillingCycle);
        fields.push(ReportDimensions.BillingIntervalDays);
      }
      if (this.isReturnRetention) {
        fields.push(ReportDimensions.BillingCycle);
        fields.push(ReportDimensions.ProductRole);
      }
    }

    return fields;
  }

  // Recursive search through nested filter category structure
  protected findFilter(model: string | number): MultiOption | undefined {
    let result: MultiOption | undefined = this.filters.find((f) => f.model === model);
    if (!result) {
      this.filters.forEach((f) => {
        if (!result && f.options) {
          const nested = (f.options as MultiOption[]).find((o: MultiOption) => o.model === model);
          if (nested) {
            result = nested;
          }
        }
      });
    }
    return result;
  }

  // Flattens nested filter categories into a single list of filter-type items
  protected getFilterItems(): MultiOption[] {
    const items: MultiOption[] = [];
    this.filters.forEach((filter) => {
      const firstOpt = filter.options && filter.options.length > 0 ? filter.options[0] : null;
      if (firstOpt && (firstOpt as MultiOption).type) {
        (filter.options as MultiOption[]).forEach((ft: MultiOption) => items.push(ft));
      } else {
        items.push(filter);
      }
    });
    return items;
  }

  // Object-based variant — V3 passes the filter reference directly; V2 passes an index
  prependSelectedOptions(indexOrFilter: number | MultiOption, searchTerm?: string) {
    const productFilter = typeof indexOrFilter === 'number' ? this.filters[indexOrFilter] : indexOrFilter;
    const existingIds = new Set<number>();

    (productFilter.options as MultiSubOption[]).forEach((opt: MultiSubOption) => {
      existingIds.add(opt.model as number);
    });

    const selectedOptions = Array.from(this.selectedProductMap.values());

    if (searchTerm) {
      selectedOptions.forEach((opt) => {
        if (!existingIds.has(opt.model)) {
          productFilter.options.push(opt);
        }
      });
    } else {
      for (let i = selectedOptions.length - 1; i >= 0; i--) {
        if (!existingIds.has(selectedOptions[i].model)) {
          productFilter.options.unshift(selectedOptions[i]);
        }
      }
    }
  }

  // Shared base payload for all getFilterOptions calls — excludes report-level fields and filters
  protected getBaseFilterOptionsQuery(): BaseFilterOptionsQuery {
    const summaryQuery = this.getQueryForSummary();
    return {
      accounts: summaryQuery['accounts'],
      date_range: summaryQuery['date_range'],
      timezone: summaryQuery['timezone'],
      report_type: summaryQuery['report_type'],
      report_version: summaryQuery['report_version'],
    };
  }

  // Builds the paginated grouped_fields payload for the product filter dropdown
  protected getProductsQueryParams(): ProductsFilterOptionsQuery {
    return {
      ...this.getBaseFilterOptionsQuery(),
      grouped_fields: [ReportDimensions.ProductID, ReportDimensions.ProductName],
      page: this.productsPage,
      page_size: 50,
    };
  }

  // Object-based variant — uses findFilter instead of index into this.filters
  getProducts({ searchTerm, nextPage }) {
    const productFilter = this.findFilter(ReportDimensions.ProductID);
    const requestSearchTerm: string = searchTerm || '';
    this.activeProductSearchTerm = requestSearchTerm;

    if (nextPage && this.hasMoreProducts) {
      this.productsPage++;
    } else {
      this.productsPage = 1;
    }

    if (productFilter && productFilter.options) {
      (productFilter.options as MultiSubOption[]).forEach((opt: MultiSubOption) => {
        if (opt.selected) {
          this.selectedProductMap.set(opt.model as number, {
            model: opt.model,
            label: opt.label,
            selected: true,
            disabled: opt.disabled,
          });
        } else {
          this.selectedProductMap.delete(opt.model as number);
        }
      });
    }

    productFilter.paginationLoading = true;

    if (searchTerm && this.productsPage === 1) {
      productFilter.options = [];
    }

    const query: ProductsFilterOptionsQuery = this.getProductsQueryParams();

    if (searchTerm) {
      const isNumeric = !isNaN(Number(searchTerm)) && searchTerm.trim() !== '';
      query.search = searchTerm;
      query.search_field = isNumeric ? ReportDimensions.ProductID : ReportDimensions.ProductName;
    }

    this.reportsService
      .getFilterOptions(query)
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        (data) => {
          if (requestSearchTerm !== this.activeProductSearchTerm) {
            productFilter.paginationLoading = false;
          } else {
            this.hasMoreProducts = data.page < data.num_pages;

            const newOptions = data.results.map((p: ProductFilterResult) => ({
              model: p[ReportDimensions.ProductID],
              label: `${p[ReportDimensions.ProductName]} (${p[ReportDimensions.ProductID]})`,
              selected: this.selectedProductMap.has(p[ReportDimensions.ProductID]),
              disabled: false,
            }));

            if (this.productsPage > 1) {
              (productFilter.options as MultiSubOption[]).push(...newOptions);
            } else {
              productFilter.options = newOptions;
            }

            productFilter.loading = false;
            productFilter.paginationLoading = false;
            this.prependSelectedOptions(productFilter, searchTerm);
          }
        },
        (error) => {
          productFilter.loading = false;
          productFilter.paginationLoading = false;
          if (error.error && error.error.detail !== 'Invalid page.') {
            this.handleError(error);
          }
        }
      );
  }

  // Overridden to recalculate nested category activeCount after removing a filter
  removeFilter(filter: MultiOption) {
    this.selectedFilters = this.selectedFilters.filter((d) => d.model !== filter.model);

    const filterItem = this.findFilter(filter.model);
    if (filterItem) {
      filterItem.searchTerm = '';
      filterItem.activeCount = 0;
      filterItem.selectedOption = null;
      filterItem.selected = false;

      if (filterItem.options) {
        filterItem.options.forEach((sub) => {
          sub.selected = false;
        });
      }

      this.filters.forEach((category) => {
        if (category.options && (category.options as MultiOption[]).includes(filterItem)) {
          category.activeCount = (category.options as MultiOption[]).reduce(
            (sum: number, ft: MultiOption) => sum + (ft.activeCount || 0),
            0
          );
        }
      });
    }

    if (this.selectedFilters.length < 5) {
      this.filters.forEach((currentOption) => {
        if (!currentOption.unavailable) {
          currentOption.disabled = false;
        }
        if (currentOption.options) {
          currentOption.options.forEach((sub) => {
            if (!(sub as MultiOption).unavailable) {
              sub.disabled = false;
            }
          });
        }
      });
    }
  }

  removeDimension(dimension: MultiSubOption) {
    super.removeDimension(dimension);
    this.fields.forEach((option: MultiOption) => {
      const hasSelected = option.options && option.options.some((sub) => sub.selected);
      if (!hasSelected) {
        option.activeCount = 0;
      }
    });
  }

  // Overridden to also reset category activeCount so the checkmark in the 1st-level list clears
  clearAllDimensions() {
    this.selectedDimensions = this.selectedDimensions.filter((d) => d.selected === false);
    this.fields.forEach((option) => {
      option.activeCount = 0;
      if (option.options) {
        option.options.forEach((sub: MultiSubOption) => {
          sub.selected = false;
          if (!sub.unavailable) {
            sub.disabled = false;
          }
        });
      }
    });
  }

  // Overridden to clear the extra nesting level (sub.options) in V3 filter categories
  clearAllFilters() {
    this.selectedFilters = [];
    this.dashboardNavFilters = {};
    this.selectedProductMap.clear();
    this.filters.forEach((option) => {
      if (!option.unavailable) {
        option.disabled = false;
      }
      option.searchTerm = '';
      option.selectedOption = null;
      option.activeCount = 0;
      option.selected = false;

      if (option.options) {
        (option.options as MultiOption[]).forEach((sub: MultiOption) => {
          sub.selected = false;
          if (!sub.unavailable) {
            sub.disabled = false;
          }
          sub.activeCount = 0;
          sub.searchTerm = '';
          if (sub.options) {
            (sub.options as MultiSubOption[]).forEach((val: MultiSubOption) => {
              val.selected = false;
              if (!val.unavailable) {
                val.disabled = false;
              }
            });
          }
        });
      }
    });
  }

  getSummaryHelpText(formattedKey: string): string {
    let helpText = '';
    this.summaryFields.forEach((key) => {
      if (this.fieldData[key]) {
        const label =
          this.fieldData[key].summary_label || this.fieldData[key].custom_label || this.fieldData[key].label;
        if (label === formattedKey) {
          helpText = this.fieldData[key].summaryHelpText || '';
        }
      }
    });
    return helpText;
  }

  isSegmentFilter(model: string | number): boolean {
    return (
      model === ReportDimensions.ProductRole ||
      model === ReportDimensions.SubscriptionAudienceID ||
      model === ReportDimensions.HoldAudienceID
    );
  }

  expandMergedDimensionFields(query: { fields: string[] }) {
    const expanded: string[] = [];
    query.fields.forEach((field: string) => {
      if (mergedDimensionFields[field]) {
        mergedDimensionFields[field].forEach((f) => expanded.push(f));
      } else {
        expanded.push(field);
      }
    });
    query.fields = expanded;
  }

  // One fetch for every dropdown that shows a name but submits an id.
  protected fetchGroupedFilterOptions(filterModel: string | number, idField: string, nameField: string) {
    const filterItem = this.findFilter(filterModel);
    const query: CRMCampaignsFilterOptionsQuery = {
      ...this.getBaseFilterOptionsQuery(),
      grouped_fields: [idField, nameField] as ReportDimensions[],
    };

    this.reportsService
      .getFilterOptions(query)
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        (data) => {
          if (filterItem) {
            const rows = data && Array.isArray(data.results) ? data.results : [];
            filterItem.options = rows.map((row: { [key: string]: number | string }) => ({
              model: row[idField],
              label: row[nameField] ? `${row[nameField]} (${row[idField]})` : String(row[idField]),
              selected: false,
              disabled: false,
            }));
            filterItem.loading = false;
          }
        },
        (error) => {
          if (filterItem) {
            filterItem.loading = false;
          }
          this.handleError(error);
        }
      );
  }

  // Overrides V2 to fetch CRM campaigns via getFilterOptions grouped_fields instead of listAll
  getCRMCampaigns() {
    const crmFilter = this.findFilter(ReportDimensions.CRMCampaignID);
    const query: CRMCampaignsFilterOptionsQuery = {
      ...this.getBaseFilterOptionsQuery(),
      grouped_fields: [ReportDimensions.CRMCampaignID, ReportDimensions.CRMCampaignName],
    };

    this.reportsService
      .getFilterOptions(query)
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        (data) => {
          if (crmFilter) {
            crmFilter.options = data.results.map((d: CRMCampaignFilterResult) => ({
              model: d[ReportDimensions.CRMCampaignID],
              label: `${d[ReportDimensions.CRMCampaignName]} (${d[ReportDimensions.CRMCampaignID]})`,
              selected: false,
              disabled: false,
            }));
            crmFilter.loading = false;
          }
        },
        (error) => {
          if (crmFilter) {
            crmFilter.loading = false;
          }
          this.handleError(error);
        }
      );
  }

  getBaseProducts() {
    const baseProductFilter = this.findFilter('base_product_id');
    const query: CRMCampaignsFilterOptionsQuery = {
      ...this.getBaseFilterOptionsQuery(),
      grouped_fields: [ReportDimensions.BaseProductID, ReportDimensions.BaseProductName],
    };

    this.reportsService
      .getFilterOptions(query)
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        (data) => {
          if (baseProductFilter) {
            baseProductFilter.options = data.results.map((d: any) => ({
              model: d[ReportDimensions.BaseProductID],
              label: d[ReportDimensions.BaseProductName]
                ? `${d[ReportDimensions.BaseProductName]} (${d[ReportDimensions.BaseProductID]})`
                : String(d[ReportDimensions.BaseProductID]),
              selected: false,
              disabled: false,
            }));
            baseProductFilter.loading = false;
          }
        },
        (error) => {
          if (baseProductFilter) {
            baseProductFilter.loading = false;
          }
          this.handleError(error);
        }
      );
  }

  // V3 fetches these options via getFilterOptionsData; skip V2 service calls
  getTrackingSources() {}
  getCancelReasonCategories() {}

  getFilterData() {
    if (!this.isGatewayReport) {
      super.getFilterData();
    }
  }

  getReport(fetchSummary: boolean = true) {
    super.getReport(fetchSummary);
    if (this.isGatewayReport) {
      this.loadOverviewCards();
      this.loadPerformanceDrivers();
    }
  }

  getQueryForSummary() {
    const query = super.getQueryForSummary();
    this.normalizeQueryFilters(query);
    return query;
  }

  get offerGroupTotals(): { impressions: number; total_rejections: number; accepted: number } {
    let impressions = 0;
    let total_rejections = 0;
    let accepted = 0;
    this.offerGroupRows.forEach((row) => {
      impressions += Number(row['impressions']) || 0;
      total_rejections += Number(row['total_rejections']) || 0;
      accepted += Number(row['accepted']) || 0;
    });
    return { impressions, total_rejections, accepted };
  }

  get offerNavFields(): { pathId: string; intent: string; funnelType: string } {
    let pathId = 'subscription_path_id';
    let intent = 'subscription_intent';
    let funnelType = 'subscription_funnel_type';
    if (this.isOrderRetention) {
      pathId = 'hold_path_id';
      intent = 'hold_intent';
      funnelType = 'hold_funnel_type';
    } else if (this.isReturnRetention) {
      pathId = 'rma_path_id';
      intent = 'rma_intent';
      funnelType = 'rma_funnel_type';
    }
    return { pathId, intent, funnelType };
  }

  private isLegacyOffer(offer: OfferGroupItem): boolean {
    let funnelType: any;
    if (this.isSubRetention) {
      funnelType = offer['subscription_funnel_type'];
    } else if (this.isReturnRetention) {
      funnelType = offer['rma_funnel_type'];
    } else {
      funnelType = offer['hold_funnel_type'];
    }
    return Number(funnelType) === OfferTypes.Legacy;
  }

  canNavigateToOffer(row: OfferGroupItem): boolean {
    const isConfirmation = Number(row[OfferNavKeys.category]) === FunnelInputTypeEnum.Confirmation;
    let result = true;
    if (isConfirmation && !(this.currentUser && this.currentUser.is_staff)) {
      result = false;
    }
    return result;
  }

  navigateToOfferStep(row: OfferGroupItem) {
    const stageId = row[OfferNavKeys.stageId];
    const category = row[OfferNavKeys.category];
    const offerId = row[OfferNavKeys.offerId];
    const isConfirmationStep = Number(category) === FunnelInputTypeEnum.Confirmation;

    const funnelType = row[OfferNavKeys.funnelType];

    let url = '';
    if (Number(funnelType) === OfferTypes.Downsell) {
      url = `/retention/stage/${stageId}/category/${funnelType}/${category}/step/${offerId}`;
    } else if (isConfirmationStep) {
      url = `/retention/stage/${stageId}/category/${funnelType}/${category}/step/${offerId}/${category}`;
    } else {
      url = `/path/${stageId}/step/edit/${offerId}`;
    }

    window.open(url, '_blank');
  }

  get isOrderRetention(): boolean {
    return this.reportType === ReportTypes.ORDER_RETENTION;
  }

  get isSubRetention(): boolean {
    return this.reportType === ReportTypes.SUBSCRIPTION_RETENTION;
  }

  get isReturnRetention(): boolean {
    return this.reportType === ReportTypes.RETURN_RETENTION;
  }

  get isRetentionReport(): boolean {
    return (
      this.reportType === ReportTypes.ORDER_RETENTION ||
      this.reportType === ReportTypes.SUBSCRIPTION_RETENTION ||
      this.reportType === ReportTypes.RETURN_RETENTION
    );
  }

  get isGatewayReport(): boolean {
    return this.reportType === ReportTypes.GATEWAY_TRANSACTION;
  }

  // Journey type sent when a Journey ID in this report opens the Customer Journey Report; null means no link.
  get journeyLinkType(): CustomerJourneyType {
    let result: CustomerJourneyType = null;
    if (this.isSubRetention) {
      result = CustomerJourneyType.Subscription;
    } else if (this.isOrderRetention) {
      result = CustomerJourneyType.Order;
    } else if (this.isReturnRetention) {
      result = CustomerJourneyType.Return;
    }
    return result;
  }

  get canRunReport(): boolean {
    return this.selectedDimensions.length > 0;
  }

  // The Details table fetches as soon as it is created, so a report can hold it back until its query is ready.
  get showDetailsTable(): boolean {
    return true;
  }

  get stageOptions(): { value: number; label: string }[] {
    let options: { value: number; label: string }[] = [];
    if (this.isSubRetention) {
      options = subRetentionStageOptions;
    } else if (this.isOrderRetention) {
      options = orderRetentionStageOptions;
    } else if (this.isReturnRetention) {
      options = returnRetentionStageOptions;
    }
    return options;
  }

  protected onCustomReportLoaded(config: ReportsUserConfiguration): void {
    const baseTitle = this.route.snapshot.data['title'] || '';
    this.guiService.setSectionTitle(baseTitle + ' - ' + config.name);
  }

  protected onReportSaveSuccess(): void {
    this.fetchAvailableReports();
  }

  fetchAvailableReports() {
    this.isLoadingReports = true;
    this.reportsConfigService
      .list({ report_type: this.reportType })
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        (data: { results: ReportsUserConfiguration[] }) => {
          if (data.results && Array.isArray(data.results)) {
            this.availableReports = data.results.filter((report: ReportsUserConfiguration) => report.report_type === this.reportType);
          }
          this.isLoadingReports = false;
        },
        (error) => {
          this.isLoadingReports = false;
          this.handleError(error);
        }
      );
  }

  openLoadDialog() {
    this.selectedLoadReportId = null;
    this.isMenuVisible = false;
    this.modalService.open('loadReportModal');
  }

  onLoadReport() {
    if (this.selectedLoadReportId) {
      const currentUrl = this.router.url;
      const baseUrl = currentUrl.split('/')[1];
      this.modalService.getModal('loadReportModal').close();
      this.router.navigate(['/' + baseUrl + '/' + this.selectedLoadReportId]);
    }
  }

  onLoadCancel() {
    this.modalService.getModal('loadReportModal').close();
  }
}
